/* Formatter pre-run checks (Spec Tighten D1 + D3, 2026-10-13).

   Before a single file is scanned: is Word holding it open, and is it a
   legacy .doc/.dot that must first become a .docx? Every selected file ends
   up in exactly one place — the run list or Not processed — so the run
   summary accounts for all of them. The bridge is injected, so the logic is
   unit-tested without Electron. */

import {
  LEGACY_WORD_NOT_PROCESSED_MESSAGE,
  WORD_LOCKED_NOT_PROCESSED_MESSAGE,
  isLegacyWordFile,
  wordLockedMessage,
} from '../../shared/wordFiles';
import type { WordConvertResult, WordLockProbe } from '../../shared/wordFiles';

export interface PreflightBridge {
  probeWordLocks?: (filePaths: string[]) => Promise<WordLockProbe[]>;
  convertWordToDocx?: (inputPath: string) => Promise<WordConvertResult>;
}

export interface PreflightResult {
  /** What the run actually reads, in selection order (a converted .doc
      appears as its temp .docx). */
  runPaths: string[];
  /** Original selected path → why it was not processed. */
  notProcessed: Record<string, string>;
  /** Run path → the file the user picked, for every converted legacy file —
      results and the report name the ORIGINAL. */
  originalByRunPath: Record<string, string>;
}

function notProcessedFor(result: Extract<WordConvertResult, { ok: false }>): string {
  if (result.reason === 'word-missing') return LEGACY_WORD_NOT_PROCESSED_MESSAGE;
  if (result.reason === 'locked') return WORD_LOCKED_NOT_PROCESSED_MESSAGE;
  return `Not processed — ${result.message}`;
}

export async function preflightFormatterInputs(
  paths: string[],
  bridge: PreflightBridge
): Promise<PreflightResult> {
  const out: PreflightResult = { runPaths: [], notProcessed: {}, originalByRunPath: {} };

  // Lock probe first — a file Word holds is never converted or scanned.
  const locked = new Map<string, string | undefined>();
  if (bridge.probeWordLocks && paths.length > 0) {
    const probes = await bridge.probeWordLocks(paths);
    for (const p of probes) if (p.status === 'locked') locked.set(p.path, p.holder);
  }

  for (const p of paths) {
    if (locked.has(p)) {
      out.notProcessed[p] = wordLockedMessage(locked.get(p));
      continue;
    }
    if (!isLegacyWordFile(p)) {
      out.runPaths.push(p);
      continue;
    }
    // .doc / .dot: Word converts it to a temp .docx (desktop only — the web
    // bridge has no converter, so the file is reported, never dropped).
    if (!bridge.convertWordToDocx) {
      out.notProcessed[p] = LEGACY_WORD_NOT_PROCESSED_MESSAGE;
      continue;
    }
    const res = await bridge.convertWordToDocx(p);
    if (res.ok) {
      out.runPaths.push(res.docxPath);
      out.originalByRunPath[res.docxPath] = p;
    } else {
      out.notProcessed[p] = notProcessedFor(res);
    }
  }
  return out;
}

/** File name only, either separator. */
export function fileBaseName(p: string): string {
  return p.split(/[\\/]/).pop() || p;
}
